import { Buildings } from "@phosphor-icons/react";
import useBranches from "@/hooks/useBranches";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin-only branch filter dropdown. For staff users it just renders their
 * locked branch as a static badge (the backend enforces the filter anyway).
 */
export default function BranchFilter({ value, onChange, testId = "branch-filter", kind = "" }) {
  const { user } = useAuth();
  const { branches } = useBranches({ activeOnly: true, kind });
  // For staff, we ALWAYS resolve the badge name from a full branch lookup
  // (no `kind` filter). Otherwise, if the user's branch type doesn't match
  // the page's `kind` (or has no branch_type_id at all), the badge would
  // fall back to "—" and confuse the operator into thinking their branch
  // is missing. The backend still enforces branch isolation so this is
  // display-only.
  const { branches: allBranches } = useBranches({ activeOnly: false });

  if (user?.role !== "admin") {
    const own =
      branches.find((b) => b.id === user?.branch_id) ||
      allBranches.find((b) => b.id === user?.branch_id);
    return (
      <div
        data-testid={`${testId}-locked`}
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-canvas-line bg-canvas-panel font-mono text-[11px] uppercase tracking-wider text-ink"
      >
        <Buildings size={12} weight="bold" />
        {own?.name || "—"}
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 border border-canvas-line bg-canvas">
      <span className="pl-3 pr-1 py-2 text-ink-muted">
        <Buildings size={12} weight="bold" />
      </span>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value || "")}
        className="pl-0 pr-3 py-2 bg-canvas text-sm focus:outline-none border-0"
      >
        <option value="">All branches</option>
        {branches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
    </div>
  );
}
